import { useLayoutEffect, useRef } from 'react';
import {
  type FieldValues,
  useFieldArray,
  useFormContext,
  useWatch
} from 'react-hook-form';
import { findFocusableElement } from 'v5/platform/dom/isFocusable';
import {
  focusAtEnd,
  PLACEHOLDER_ROW_KEY,
  useRowListKeyboard
} from './useRowListKeyboard';

export type RowListFieldArrayOptions<TRow extends FieldValues> = {
  /** The array's path in the enclosing form (`FormProvider`). */
  name: string;
  /** Stable per row: the row's `data-row-key`. */
  rowKeyOf(row: TRow): string;
  /** What Enter needs to know of a row: blank (an empty last row is dropped) or in error (and the part to show it at). */
  rowFacts(
    row: TRow,
    index: number
  ): { blank: boolean; error: { part: string } | null };
  /** Enter stopped at a row in error: have the form show why. */
  onShowError(index: number): void;
  /** `false`: the order isn't the person's to change (no ⌥↑ / ⌥↓). Default `true`. */
  reorderable?: boolean;
  /** The part a row just added focuses; the first control of the row without it. */
  focusPart?: string;
};

/**
 * A row list kept in a react-hook-form array, in the person's own order: the
 * row-list keyboard wired to the array, a row just added taking the focus,
 * the empty row at the end re-created per row so it starts blank. Knows no
 * row shape and no field: the host renders the rows (`RowListSortableRow`,
 * `RowListRow`) and the empty row (`PLACEHOLDER_ROW_KEY`, `placeholderKey`).
 */
export function useRowListFieldArray<TRow extends FieldValues>({
  name,
  rowKeyOf,
  rowFacts,
  onShowError,
  reorderable = true,
  focusPart
}: RowListFieldArrayOptions<TRow>) {
  const form = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name
  });
  const rows: TRow[] =
    useWatch<Record<string, TRow[] | undefined>>({ name }) ?? [];
  const rowKeys = rows.map(rowKeyOf);
  const pendingFocus = useRef<string | null>(null);

  const keyboard = useRowListKeyboard({
    insert: 'end',
    ...(reorderable
      ? {
          manualOrder: true as const,
          onMove(rowKey: string, direction: -1 | 1) {
            const index = rowKeys.indexOf(rowKey);
            if (index !== -1) move(index, index + direction);
          }
        }
      : { manualOrder: false as const }),
    rowKeys,
    rowFacts(rowKey) {
      const index = rowKeys.indexOf(rowKey);
      const row = rows[index];
      return row === undefined
        ? { blank: true, error: null, unacknowledgedHint: false }
        : { ...rowFacts(row, index), unacknowledgedHint: false };
    },
    onShowError(rowKey) {
      const index = rowKeys.indexOf(rowKey);
      if (index !== -1) onShowError(index);
    },
    onRemove(rowKey) {
      const index = rowKeys.indexOf(rowKey);
      if (index !== -1) remove(index);
    }
  });

  // A row just added takes the focus: its `focusPart`, caret at the end.
  useLayoutEffect(() => {
    const rowKey = pendingFocus.current;
    const list = keyboard.listProps.ref.current;
    if (rowKey === null || !list) return;
    const target = list.querySelector<HTMLElement>(
      `[data-row-key="${CSS.escape(rowKey)}"][data-row-part${
        focusPart === undefined ? '' : `="${CSS.escape(focusPart)}"`
      }]`
    );
    const control = target && findFocusableElement(target);
    if (!control) return;
    pendingFocus.current = null;
    focusAtEnd(control);
  });

  function add(row: TRow) {
    pendingFocus.current = rowKeyOf(row);
    append(row, { shouldFocus: false });
  }

  return {
    /** RHF's keys for the rows, for `SortableRows` ids and React keys. */
    fields,
    rows,
    rowKeys,
    /** Spread on `RowList`. */
    listProps: keyboard.listProps,
    add,
    remove,
    move,
    /** The empty row's React key: it changes with every row added. */
    placeholderKey: `${PLACEHOLDER_ROW_KEY}-${fields.length}`
  };
}
